import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  gradient: string;
  delay: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  features,
  gradient,
  delay,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className="ai-feature-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay }}
      whileHover={prefersReducedMotion ? {} : { y: -10, scale: 1.02 }}
    >
      {/* Card glow effect */}
      <div className="card-glow" style={{ background: gradient }} />

      {/* Animated border */}
      <div className="card-border">
        <motion.div
          className="border-gradient"
          style={{ background: gradient }}
          animate={prefersReducedMotion ? {} : {
            rotate: [0, 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear" as const,
          }}
        />
      </div>

      <div className="card-inner">
        {/* AI Badge */}
        <div className="ai-feature-badge">
          <span className="badge-dot" />
          AI-Powered
        </div>

        {/* Icon */}
        <motion.div
          className="feature-icon"
          style={{ background: gradient }}
          whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          {icon}
        </motion.div>

        {/* Title */}
        <h3 className="feature-title">{title}</h3>

        {/* Description */}
        <p className="feature-description">{description}</p>

        {/* Feature list */}
        <ul className="feature-list">
          {features.map((feature, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: delay + 0.1 * index }}
            >
              <span className="check-icon" style={{ background: gradient }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              {feature}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const AIFeatures: React.FC = () => {
  const features = [
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M12 18v-6" />
          <path d="M9 15l3-3 3 3" />
        </svg>
      ),
      title: "One-Click Listing",
      description: "Enter an ASIN and GPT-4o creates a complete eBay listing — optimised title, description, category, and item specifics — in under 3 seconds.",
      features: [
        "Instant product data extraction",
        "AI-generated titles & descriptions",
        "Automatic image optimisation",
        "Multi-marketplace publishing",
      ],
      gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      delay: 0,
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      title: "Smart Price Calculation",
      description: "AI calculates your optimal selling price using margin rules, eBay fees, VAT settings, and minimum profit thresholds. Multiple rounding options for professional pricing.",
      features: [
        "Dynamic profit margin calculation",
        "Competitor price analysis",
        "Shipping cost integration",
        "Currency conversion included",
      ],
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      delay: 0.15,
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      ),
      title: "Category Matching",
      description: "GPT-4o maps products to the most accurate eBay category from 20,000+ options with 98%+ accuracy. Item specifics are filled automatically.",
      features: [
        "GPT-4o powered classification",
        "Cross-marketplace mapping",
        "Item specifics auto-fill",
        "Compliance checking",
      ],
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      delay: 0.3,
    },
  ];

  return (
    <section className="ai-features-section" id="ai-features">
      <Container>
        {/* Section Header */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </span>
            <span>AI-Powered Features</span>
          </div>
          <h2 className="section-title">
            Supercharge your workflow with <span className="gradient-text">artificial intelligence</span>
          </h2>
          <p className="section-subtitle">
            Our GPT-4o powered engine handles the heavy lifting, so you can focus on growing your business.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <Row className="feature-cards-row">
          {features.map((feature, index) => (
            <Col lg={4} md={6} key={index} className="mb-4">
              <FeatureCard {...feature} />
            </Col>
          ))}
        </Row>

        {/* Bottom Stats */}
        <motion.div
          className="ai-stats-bar"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="ai-stat">
            <span className="stat-number">98.7%</span>
            <span className="stat-label">Category Accuracy</span>
          </div>
          <div className="stat-divider" />
          <div className="ai-stat">
            <span className="stat-number">&lt;3s</span>
            <span className="stat-label">Average Listing Time</span>
          </div>
          <div className="stat-divider" />
          <div className="ai-stat">
            <span className="stat-number">24/7</span>
            <span className="stat-label">Automated Sync</span>
          </div>
          <div className="stat-divider" />
          <div className="ai-stat">
            <span className="stat-number">GPT-4o</span>
            <span className="stat-label">AI Model</span>
          </div>
        </motion.div>
      </Container>

      {/* Background decoration */}
      <div className="ai-features-bg">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
      </div>
    </section>
  );
};

export default AIFeatures;
